/**
 * Table displaying conformance errors from the run and its child runs, with numbered rule badges
 * that correspond to the same numbers used in the Failed Records table.
 */

import Badge from "@cloudscape-design/components/badge"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table from "@cloudscape-design/components/table"
import type { GetRunResponseContent, RuleEntry, Severity } from "./types"
import CollapsibleRule from "./CollapsibleRule"
import { formatConformanceMessage } from "./formatConformanceMessage"

type BadgeColor = React.ComponentProps<typeof Badge>["color"]

interface ConformanceErrorsTableProps {
  /**
   * The full run response including child runs.
   */
  run: GetRunResponseContent
  /**
   * Shared rule numbering map built by the parent orchestrator.
   */
  ruleMap: Map<string, RuleEntry>
}

interface CollectedError {
  id: string
  severity: Severity
  rule: string
  message: string
}

const severityColor = (severity: Severity): BadgeColor =>
  severity === "major" ? "severity-high" : "severity-medium"

/**
 * Collects conformance errors from the parent run and all child runs, then renders a table with a
 * numbered badge, Severity, Rule, and Message columns.
 */
export default function ConformanceErrorsTable({ run, ruleMap }: ConformanceErrorsTableProps) {
  const allErrors: CollectedError[] = []

  run.conformanceErrors?.forEach((err, i) => {
    allErrors.push({
      id: `p-${i}`,
      severity: err.stage as Severity,
      rule: err.rule,
      message: err.message,
    })
  })
  run.childRuns?.forEach((child, ci) => {
    child.conformanceErrors?.forEach((err, i) => {
      allErrors.push({
        id: `c-${ci}-${i}`,
        severity: err.stage as Severity,
        rule: err.rule,
        message: err.message,
      })
    })
  })

  if (allErrors.length === 0) return null

  const majorCount = allErrors.filter((e) => e.severity === "major").length
  const minorCount = allErrors.filter((e) => e.severity === "minor").length

  return (
    <Container
      header={
        <Header
          counter={`(${allErrors.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {majorCount > 0 && <Badge color="severity-high">{majorCount} major</Badge>}
              {minorCount > 0 && <Badge color="severity-medium">{minorCount} minor</Badge>}
            </SpaceBetween>
          }
        >
          Conformance Errors
        </Header>
      }
    >
      <Table
        items={allErrors}
        wrapLines
        columnDefinitions={[
          {
            id: "number",
            header: "#",
            width: 60,
            cell: (err) => {
              const entry = ruleMap.get(err.rule)
              return entry ? <Badge color={severityColor(entry.severity)}>{entry.num}</Badge> : ""
            },
          },
          {
            id: "severity",
            header: "Severity",
            width: 100,
            cell: (err) => (
              <Badge color={severityColor(err.severity)}>
                {err.severity === "major" ? "MAJOR" : "MINOR"}
              </Badge>
            ),
          },
          {
            id: "rule",
            header: "Rule",
            minWidth: 360,
            cell: (err) => <CollapsibleRule rule={err.rule} />,
          },
          {
            id: "message",
            header: "Message",
            width: 280,
            cell: (err) => formatConformanceMessage(err.message, err.rule),
          },
        ]}
        variant="embedded"
        trackBy="id"
      />
    </Container>
  )
}
